import { useEffect, useState, useMemo, useRef } from "react";
import {
    Plus,
    Loader2,
    Maximize2,
    Building2,
    X,
    Trash2,
    DollarSign,
    User,
    FileSignature,
    Calendar,
    ClipboardList,
    Target,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { TID } from "@/lib/testIds";
import { useRealtimeChannel } from "@/context/RealtimeContext";
import FocusHeader from "@/components/FocusHeader";
import ExpandCard from "@/components/ExpandCard";

const STAGE_META = {
    lead: { bg: "#333333", fg: "#A1A1AA", label: "LEAD" },
    qualified: { bg: "#2979FF", fg: "#050505", label: "QUALIFIED" },
    proposal: { bg: "#FFEA00", fg: "#050505", label: "PROPOSAL" },
    negotiation: { bg: "#FF5500", fg: "#050505", label: "NEGOTIATION" },
    "closed-won": { bg: "#00E676", fg: "#050505", label: "WON" },
    "closed-lost": { bg: "#FF1744", fg: "#FFFFFF", label: "LOST" },
    "on-hold": { bg: "#52525B", fg: "#FFFFFF", label: "ON HOLD" },
};
const STAGE_LIST = Object.keys(STAGE_META);

const CONTRACT_META = {
    rfp: "RFP",
    "sole-source": "SOLE-SOURCE",
    "commercial-pilot": "COMMERCIAL PILOT",
    "follow-on": "FOLLOW-ON",
    sbir: "SBIR",
    ota: "OTA",
    other: "OTHER",
};
const CONTRACT_LIST = Object.keys(CONTRACT_META);

function StagePill({ value, onClick }) {
    const s = STAGE_META[value] || STAGE_META.lead;
    return (
        <button
            onClick={onClick}
            type="button"
            className="inline-flex items-center justify-center px-2 py-1 min-w-[110px] font-mono text-[10px] tracking-[0.16em] font-bold hover:opacity-90"
            style={{ backgroundColor: s.bg, color: s.fg }}
        >
            {s.label}
        </button>
    );
}

function money(v) {
    if (v == null) return "—";
    if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
    return `$${v}`;
}

export default function BDPipeline() {
    const [deals, setDeals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [expanded, setExpanded] = useState(null);
    const flatRef = useRef([]);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/bd/deals");
            setDeals(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load deals.");
        } finally {
            setLoading(false);
        }
    };

    useRealtimeChannel("bd", load);

    useEffect(() => {
        load();
    }, []);

    const indexed = useMemo(
        () => deals.map((d, i) => ({ ...d, index: i + 1 })),
        [deals]
    );

    useEffect(() => {
        flatRef.current = indexed;
    }, [indexed]);

    useEffect(() => {
        const onExpand = (e) => {
            const n = Number(e.detail?.index);
            const target = flatRef.current.find((x) => x.index === n);
            if (!target) return toast.warning(`Deal #${n} not found.`);
            setExpanded(target);
        };
        const onCollapse = () => setExpanded(null);
        const onRefresh = () => load();
        window.addEventListener("virtus:expand-deal", onExpand);
        window.addEventListener("virtus:collapse-deal", onCollapse);
        window.addEventListener("virtus:deals-refreshed", onRefresh);
        return () => {
            window.removeEventListener("virtus:expand-deal", onExpand);
            window.removeEventListener("virtus:collapse-deal", onCollapse);
            window.removeEventListener("virtus:deals-refreshed", onRefresh);
        };
    }, []);

    const save = async (data) => {
        try {
            if (editing?.id) {
                const { data: u } = await api.patch(`/bd/deals/${editing.id}`, data);
                setDeals((prev) => prev.map((x) => (x.id === u.id ? u : x)));
            } else {
                const { data: c } = await api.post("/bd/deals", data);
                setDeals((prev) => [c, ...prev]);
            }
            setShowModal(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed.");
        }
    };

    const remove = async () => {
        if (!editing?.id) return;
        try {
            await api.delete(`/bd/deals/${editing.id}`);
            setDeals((prev) => prev.filter((x) => x.id !== editing.id));
            setShowModal(false);
        } catch (e) {
            toast.error("Delete failed.");
        }
    };

    const cycleStage = async (d) => {
        const next = STAGE_LIST[(STAGE_LIST.indexOf(d.stage) + 1) % STAGE_LIST.length];
        try {
            const { data: u } = await api.patch(`/bd/deals/${d.id}`, { stage: next });
            setDeals((prev) => prev.map((x) => (x.id === u.id ? u : x)));
        } catch (e) {
            toast.error("Update failed.");
        }
    };

    const total = deals.length;
    const active = deals.filter((d) =>
        ["lead", "qualified", "proposal", "negotiation"].includes(d.stage)
    ).length;
    const won = deals.filter((d) => d.stage === "closed-won").length;
    const pipelineValue = deals
        .filter((d) => ["lead", "qualified", "proposal", "negotiation"].includes(d.stage))
        .reduce((sum, d) => sum + (d.value_estimate || 0), 0);

    return (
        <div className="min-h-screen md:h-screen bg-virtus-bg text-white flex flex-col md:overflow-hidden">
            <FocusHeader
                code="AO/08"
                title="BD Pipeline"
                subtitle="Business development · contracts · deal flow · RESTRICTED"
            />

            <div className="grid grid-cols-2 md:grid-cols-4 border-b border-virtus-line flex-shrink-0">
                <Metric label="TOTAL" value={total} color="#FFFFFF" />
                <Metric label="ACTIVE" value={active} color="#FFEA00" />
                <Metric label="WON" value={won} color="#00E676" />
                <MetricText label="PIPELINE $" value={money(pipelineValue)} color="#FF5500" />
            </div>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between px-3 md:px-6 py-2 border-b border-virtus-line flex-shrink-0 gap-2 md:gap-0">
                <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim">
                    DEALS // SAY <span className="text-virtus-orange">"EXPAND DEAL N"</span> · <span className="text-virtus-orange">"ADVANCE DEAL N"</span>
                </div>
                <button
                    onClick={() => {
                        setEditing(null);
                        setShowModal(true);
                    }}
                    className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange border border-virtus-orange px-3 py-1.5 hover:bg-virtus-orange hover:text-black"
                    data-testid={TID.bdAddBtn}
                >
                    <Plus size={11} /> ADD DEAL
                </button>
            </div>

            <div className="flex-1 overflow-y-auto">
                {loading ? (
                    <div className="py-16 flex items-center justify-center text-virtus-dim">
                        <Loader2 className="animate-spin" />
                    </div>
                ) : indexed.length === 0 ? (
                    <div className="mx-6 my-8 border border-dashed border-virtus-line p-12 text-center">
                        <Building2 size={40} className="mx-auto text-virtus-dim mb-4" />
                        <div className="font-heading text-xl uppercase tracking-tight text-white mb-2">
                            Pipeline empty
                        </div>
                        <div className="text-sm text-virtus-dim mb-6">
                            Add your first deal. Track RFPs, sole-sources, SBIRs, and commercial pilots through close.
                        </div>
                        <button
                            onClick={() => setShowModal(true)}
                            className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange border border-virtus-orange px-4 py-2 hover:bg-virtus-orange hover:text-black"
                        >
                            <Plus size={12} className="inline mr-2" /> ADD FIRST DEAL
                        </button>
                    </div>
                ) : (
                    <div>
                        <div className="grid grid-cols-[52px_1fr_120px_110px_100px_140px_120px_120px_36px] items-center gap-2 px-4 h-[26px] border-b border-virtus-line font-mono text-[9px] tracking-[0.24em] uppercase text-virtus-faint sticky top-0 bg-virtus-bg z-10">
                            <div>#</div>
                            <div>CLIENT</div>
                            <div>VALUE</div>
                            <div>TYPE</div>
                            <div>STAGE</div>
                            <div>NEXT ACTION</div>
                            <div>NEXT DATE</div>
                            <div>OWNER</div>
                            <div />
                        </div>
                        <AnimatePresence>
                            {indexed.map((d) => (
                                <motion.div
                                    key={d.id}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    onClick={() => {
                                        setEditing(d);
                                        setShowModal(true);
                                    }}
                                    className="grid grid-cols-[52px_1fr_120px_110px_100px_140px_120px_120px_36px] items-center gap-2 px-4 h-9 border-b border-virtus-line hover:bg-[#141414] cursor-pointer group"
                                    style={{
                                        boxShadow: `inset 3px 0 0 ${
                                            (STAGE_META[d.stage] || STAGE_META.lead).bg
                                        }`,
                                    }}
                                    data-testid={TID.bdDealRow(d.id)}
                                >
                                    <div className="font-mono text-[10px] tracking-[0.2em] font-bold text-virtus-orange">
                                        #{String(d.index).padStart(2, "0")}
                                    </div>
                                    <div className="text-sm text-white group-hover:text-virtus-orange transition-colors truncate">
                                        {d.client_name}
                                    </div>
                                    <div className="font-mono text-sm text-virtus-orange">
                                        {money(d.value_estimate)}
                                    </div>
                                    <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-virtus-dim truncate">
                                        {CONTRACT_META[d.contract_type] || "—"}
                                    </div>
                                    <div onClick={(e) => { e.stopPropagation(); cycleStage(d); }}>
                                        <StagePill value={d.stage} />
                                    </div>
                                    <div className="text-xs text-virtus-dim truncate">
                                        {d.next_action || "—"}
                                    </div>
                                    <div className="font-mono text-xs text-virtus-dim">
                                        {d.next_action_date || "—"}
                                    </div>
                                    <div className="font-mono text-xs text-virtus-dim truncate">
                                        {d.owner || "—"}
                                    </div>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setExpanded(d);
                                        }}
                                        className="opacity-0 group-hover:opacity-100 text-virtus-dim hover:text-virtus-orange"
                                        aria-label="Expand deal"
                                    >
                                        <Maximize2 size={12} />
                                    </button>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>
                )}
            </div>

            <DealModal
                open={showModal}
                deal={editing}
                onClose={() => setShowModal(false)}
                onSave={save}
                onDelete={remove}
            />
            <ExpandCard
                open={!!expanded}
                onClose={() => setExpanded(null)}
                onEdit={() => {
                    setEditing(expanded);
                    setShowModal(true);
                    setExpanded(null);
                }}
                code={`DEAL #${String(expanded?.index || 0).padStart(2, "0")}`}
                ribbonExtras={
                    expanded ? (
                        <>
                            <span className="text-virtus-line">//</span>
                            <div className="font-mono text-xs tracking-[0.24em] uppercase text-virtus-dim">
                                {CONTRACT_META[expanded.contract_type] || "—"}
                            </div>
                        </>
                    ) : null
                }
                title={expanded?.client_name}
                pills={expanded ? [<StagePill value={expanded.stage} />] : []}
                detailBlocks={
                    expanded
                        ? [
                              { icon: DollarSign, label: "Value", value: money(expanded.value_estimate), accent: "#FF5500" },
                              { icon: FileSignature, label: "Contract", value: CONTRACT_META[expanded.contract_type] || "—" },
                              { icon: User, label: "POC", value: expanded.point_of_contact },
                              { icon: Calendar, label: "Close target", value: expanded.close_target_date },
                          ]
                        : []
                }
                sections={
                    expanded
                        ? [
                              { icon: Target, label: "Next action", value: expanded.next_action },
                              { icon: Calendar, label: "Next action date", value: expanded.next_action_date },
                              { icon: User, label: "Owner", value: expanded.owner },
                              { icon: ClipboardList, label: "Notes", value: expanded.notes },
                          ]
                        : []
                }
                testId="bd-expand-overlay"
                closeTestId="bd-expand-close-btn"
                editTestId="bd-expand-edit-btn"
            />
        </div>
    );
}

function Metric({ label, value, color }) {
    return (
        <div className="px-5 py-2 border-r border-virtus-line last:border-r-0 flex items-baseline gap-4">
            <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-virtus-faint">
                {label}
            </div>
            <div className="font-mono font-bold text-2xl" style={{ color }}>
                {String(value).padStart(2, "0")}
            </div>
        </div>
    );
}

function MetricText({ label, value, color }) {
    return (
        <div className="px-5 py-2 border-r border-virtus-line last:border-r-0 flex items-baseline gap-4">
            <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-virtus-faint">
                {label}
            </div>
            <div className="font-mono font-bold text-2xl truncate" style={{ color }}>
                {value}
            </div>
        </div>
    );
}

function DealModal({ open, deal, onClose, onSave, onDelete }) {
    const [f, setF] = useState(deal || {});
    const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

    if (open && deal && deal.id !== f.id) setF(deal);
    if (open && !deal && f.id) setF({});

    if (!open) return null;
    const isNew = !deal?.id;

    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 backdrop-blur-md">
            <div className="w-[600px] max-w-[92vw] bg-[#0A0A0A] border-2 border-virtus-orange">
                <div className="flex items-center justify-between px-5 py-3 border-b border-virtus-line">
                    <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange">
                        {isNew ? "NEW DEAL" : "EDIT DEAL"}
                    </div>
                    <button onClick={onClose} className="text-virtus-dim hover:text-virtus-orange" aria-label="Close">
                        <X size={16} />
                    </button>
                </div>
                <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
                    <Field label="Client / customer">
                        <input
                            value={f.client_name || ""}
                            onChange={(e) => set("client_name", e.target.value)}
                            placeholder="Client 1"
                            autoFocus
                            className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none"
                            data-testid={TID.dealClientInput}
                        />
                    </Field>
                    <div className="grid grid-cols-3 gap-3">
                        <Field label="Stage">
                            <select
                                value={f.stage || "lead"}
                                onChange={(e) => set("stage", e.target.value)}
                                className="w-full bg-[#0A0A0A] border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white font-mono text-xs outline-none"
                            >
                                {STAGE_LIST.map((s) => (
                                    <option key={s} value={s}>{STAGE_META[s].label}</option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Contract type">
                            <select
                                value={f.contract_type || "rfp"}
                                onChange={(e) => set("contract_type", e.target.value)}
                                className="w-full bg-[#0A0A0A] border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white font-mono text-xs outline-none"
                            >
                                {CONTRACT_LIST.map((c) => (
                                    <option key={c} value={c}>{CONTRACT_META[c]}</option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Value estimate ($)">
                            <input
                                type="number"
                                min="0"
                                value={f.value_estimate ?? ""}
                                onChange={(e) => set("value_estimate", e.target.value === "" ? null : parseFloat(e.target.value))}
                                placeholder="250000"
                                className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none font-mono"
                            />
                        </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Point of contact">
                            <input
                                value={f.point_of_contact || ""}
                                onChange={(e) => set("point_of_contact", e.target.value)}
                                placeholder="Contact 1"
                                className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none"
                            />
                        </Field>
                        <Field label="Owner (BD lead)">
                            <input
                                value={f.owner || ""}
                                onChange={(e) => set("owner", e.target.value)}
                                placeholder="Owner"
                                className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none"
                            />
                        </Field>
                    </div>
                    <Field label="Next action">
                        <input
                            value={f.next_action || ""}
                            onChange={(e) => set("next_action", e.target.value)}
                            placeholder="Action 1"
                            className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none"
                        />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Next action date">
                            <input
                                type="date"
                                value={f.next_action_date || ""}
                                onChange={(e) => set("next_action_date", e.target.value)}
                                className="w-full bg-[#0A0A0A] border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white font-mono text-xs outline-none"
                            />
                        </Field>
                        <Field label="Close target date">
                            <input
                                type="date"
                                value={f.close_target_date || ""}
                                onChange={(e) => set("close_target_date", e.target.value)}
                                className="w-full bg-[#0A0A0A] border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white font-mono text-xs outline-none"
                            />
                        </Field>
                    </div>
                    <Field label="Notes">
                        <textarea
                            value={f.notes || ""}
                            onChange={(e) => set("notes", e.target.value)}
                            rows={3}
                            className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none"
                        />
                    </Field>
                </div>
                <div className="flex items-center justify-between px-5 py-3 border-t border-virtus-line">
                    <div>
                        {!isNew && (
                            <button
                                onClick={onDelete}
                                className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-stuck hover:text-white px-3 py-2 border border-virtus-stuck hover:bg-virtus-stuck"
                                data-testid={TID.dealDeleteBtn}
                            >
                                <Trash2 size={11} className="inline mr-1" /> DELETE
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={onClose}
                            className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-dim hover:text-white px-3 py-2"
                        >
                            CANCEL
                        </button>
                        <button
                            onClick={() => {
                                if (!f.client_name?.trim()) return toast.warning("Client name required.");
                                onSave({
                                    client_name: f.client_name.trim(),
                                    stage: f.stage || "lead",
                                    value_estimate: f.value_estimate ?? null,
                                    contract_type: f.contract_type || "rfp",
                                    point_of_contact: f.point_of_contact || null,
                                    next_action: f.next_action || null,
                                    next_action_date: f.next_action_date || null,
                                    close_target_date: f.close_target_date || null,
                                    owner: f.owner || null,
                                    notes: f.notes || "",
                                });
                            }}
                            className="font-mono text-[10px] tracking-[0.22em] uppercase bg-virtus-orange text-black px-4 py-2 hover:bg-white glow-orange"
                            data-testid={TID.dealSaveBtn}
                        >
                            {isNew ? "CREATE" : "SAVE"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-dim mb-1.5">
                {label}
            </div>
            {children}
        </div>
    );
}
